import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import util from "../../utils/miniUtils";
import { formatDateTime, showApiError } from "../sales/salesHelpers";
import "../sales/Sales.css";

// FAILED_EMAILS_MAX_LIMIT de inventory-api es 100
const PAGE_SIZES = [ 10, 25, 50, 100 ];
const DEFAULT_PAGE_SIZE = 10;

// email_queue.template de inventory-api (§11 de requerimientos-mercadopago.md)
const EMAIL_TEMPLATE_LABELS = {
    "C-1": "Activación de cuenta",
    "C-2": "Recuperar contraseña",
    "C-3": "Confirmación de pedido",
    "C-4": "Pago en revisión",
    "C-5": "Pedido enviado",
    "C-6": "Reembolso",
    "A-1": "Admin: nueva venta",
    "A-2": "Admin: requiere atención",
};

// Ajustes de la tienda (solo Admin): interruptor de venta en línea y correos que agotaron sus reintentos
export default function Settings() {
    const [ onlineSales, setOnlineSales ] = useState( null );
    const [ failedEmails, setFailedEmails ] = useState( [] );
    const [ failedTotal, setFailedTotal ] = useState( 0 );
    const [ page, setPage ] = useState( 1 );
    const [ pageSize, setPageSize ] = useState( DEFAULT_PAGE_SIZE );
    const [ loading, setLoading ] = useState( false );

    useEffect( () => {
        loadOnlineSales().then( null );
    }, [] );

    useEffect( () => {
        loadFailedEmails().then( null );
    }, [ page, pageSize ] );

    async function loadOnlineSales() {
        try {
            const request = await util.axios.get( "settings/online-sales" );
            const { error, msg, data } = request.data;
            if ( error ) throw msg;
            setOnlineSales( data );
        } catch ( e ) {
            showApiError( e );
        }
    }

    async function loadFailedEmails() {
        try {
            setLoading( true );
            const params = { page, limit: pageSize };
            const request = await util.axios.get( "notifications/emails/failed", { params } );
            const { error, msg, data } = request.data;
            if ( error ) throw msg;
            setFailedEmails( data.emails );
            setFailedTotal( data.total );
        } catch ( e ) {
            showApiError( e );
        } finally {
            setLoading( false );
        }
    }

    const confirmAndToggleOnlineSales = async () => {
        const turningOn = !onlineSales.enabled;
        const result = await Swal.fire( {
            icon: "warning",
            title: turningOn ? "¿Activar la venta en línea?" : "¿Apagar la venta en línea?",
            text: turningOn
                ? "La tienda volverá a aceptar pedidos y pagos."
                : "La tienda se mostrará como catálogo y no aceptará pedidos nuevos. Los pagos en curso se siguen procesando.",
            showCancelButton: true,
            confirmButtonText: turningOn ? "Sí, activar" : "Sí, apagar",
            cancelButtonText: "Cancelar",
            customClass: {
                confirmButton: turningOn ? "btn btn-success" : "btn btn-danger",
                cancelButton: "btn btn-secondary ms-2",
            },
            buttonsStyling: false,
        } );
        if ( !result.isConfirmed ) return;

        try {
            setLoading( true );
            const request = await util.axios.put( "settings/online-sales", { enabled: turningOn } );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            util.toast().success( msg );
            await loadOnlineSales();
        } catch ( e ) {
            showApiError( e );
        } finally {
            setLoading( false );
        }
    };

    const handlePageSizeChange = ( event ) => {
        setPage( 1 );
        setPageSize( Number( event.target.value ) );
    };

    const totalPages = Math.ceil( failedTotal / pageSize );
    const isFirstPage = page <= 1;
    const isLastPage = page >= totalPages;

    return (
        <div className="container-fluid mb-3 mt-3">
            <div className="card mb-4" data-cy="settings-online-sales">
                <div className="card-body">
                    <h5 className="card-title">Venta en línea</h5>
                    {onlineSales && (
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="form-check form-switch mb-0">
                                <input id="settings-online-sales-switch" type="checkbox" role="switch" className="form-check-input"
                                    checked={onlineSales.enabled} onChange={confirmAndToggleOnlineSales} disabled={loading}
                                    data-cy="settings-online-sales-switch" />
                                <label htmlFor="settings-online-sales-switch" className="form-check-label">
                                    {onlineSales.enabled ? "Encendida: la tienda acepta pedidos" : "Apagada: la tienda se muestra como catálogo"}
                                </label>
                            </div>
                            <span className="small text-muted" data-cy="settings-online-sales-last-change">
                                Último cambio: {onlineSales.updatedByName || "—"} · {formatDateTime( onlineSales.updatedAt )}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <h5>Correos no enviados <span className="text-muted small">({failedTotal})</span></h5>
            <div className="table-responsive">
                <table className="table table-sm table-hover align-middle sales-table" data-cy="settings-failed-emails">
                    <thead>
                        <tr>
                            <th className="text-end">#</th>
                            <th>Destinatario</th>
                            <th>Tipo</th>
                            <th>Fecha</th>
                            <th>Intentos</th>
                            <th>Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        {failedEmails.length === 0 && !loading && (
                            <tr><td colSpan={6} className="text-center text-muted">No hay correos fallidos</td></tr>
                        )}
                        {failedEmails.map( ( email, position ) => (
                            <tr key={email.id}>
                                <td className="text-end text-muted">{( page - 1 ) * pageSize + position + 1}</td>
                                <td>{( email.recipients || [] ).join( ", " ) || "—"}</td>
                                <td>{EMAIL_TEMPLATE_LABELS[email.template] || email.template}</td>
                                <td>{formatDateTime( email.updated_at )}</td>
                                <td>{email.attempts}</td>
                                <td className="small text-danger">{email.last_error}</td>
                            </tr>
                        ) )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex align-items-center gap-2" data-cy="settings-failed-emails-pagination">
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isFirstPage} onClick={() => setPage( page - 1 )}
                    data-cy="settings-failed-emails-prev">Anterior</button>
                <span className="small">Página {totalPages === 0 ? 0 : page} de {totalPages}</span>
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isLastPage} onClick={() => setPage( page + 1 )}
                    data-cy="settings-failed-emails-next">Siguiente</button>
                <label htmlFor="settings-failed-emails-page-size" className="small ms-3">Mostrar</label>
                <select id="settings-failed-emails-page-size" className="form-select form-select-sm sales-page-size" value={pageSize}
                    onChange={handlePageSizeChange} data-cy="settings-failed-emails-page-size">
                    {PAGE_SIZES.map( ( size ) => <option key={size} value={size}>{size}</option> )}
                </select>
                <span className="small">por página</span>
            </div>

            {util.LOADING_SCREEN( loading )}
        </div>
    );
}
